import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Store } from '@ngrx/store';
import { throwError, Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, filter, take, switchMap, distinctUntilChanged, startWith } from 'rxjs/operators';

import { ConfigService, SentryLogger, AppState } from '@fcom/core';
import { loginToken } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx';

import {
  LinkedAccount,
  LinkedAccountsResponse,
  LinkingStatus,
} from '../interfaces/avios-linking-and-exchange.interface';

export enum AviosAccountLinkingStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  LOADING = 'loading',
}

@Injectable()
export class AviosLinkingService {
  private accessToken$: Observable<string>;

  private updateStatus$ = new BehaviorSubject<'loading' | 'refresh'>('refresh');

  status$: Observable<AviosAccountLinkingStatus> = this.updateStatus$.pipe(
    distinctUntilChanged(),
    switchMap((updateStatus) => {
      if (updateStatus === 'loading') {
        return of(AviosAccountLinkingStatus.LOADING);
      }

      return this.getAviosLinkedAccounts().pipe(
        map((linkedAccount) => {
          return linkedAccount.linkingStatus === LinkingStatus.ACTIVE
            ? AviosAccountLinkingStatus.ACTIVE
            : AviosAccountLinkingStatus.INACTIVE;
        })
      );
    }),
    startWith(AviosAccountLinkingStatus.LOADING),
    finShare()
  );

  constructor(
    private http: HttpClient,
    private sentryLogger: SentryLogger,
    private store$: Store<AppState>,
    private configService: ConfigService
  ) {
    this.accessToken$ = this.store$.pipe(loginToken(), filter(Boolean), take(1));
  }

  getAviosLinkedAccounts(): Observable<LinkedAccount> {
    const apiUrl = this.configService.cfg.loyaltyServices.legacy.loyaltyAviosLinkingUrl;
    const xApiKey = this.configService.cfg.loyaltyServices.legacy.apiProfileApiKey;

    return this.accessToken$.pipe(
      switchMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'x-api-key': xApiKey,
            oauth_token: token,
            Authorization: `Bearer ${btoa(token)}`,
          }),
        };

        return this.http.get<LinkedAccountsResponse>(apiUrl, httpOptions).pipe(
          map((response) => {
            return response.linkedAccounts[0];
          }),
          catchError((err) => {
            this.sentryLogger.warn(err.message, { error: err });
            return throwError(() => err);
          })
        );
      })
    );
  }
}
